'use client';

import { IntentLink } from '@/core/i18n/navigation';
import NextImage from 'next/image';
import { Button } from '@/core/components/ui/button';
import { Skeleton } from '@/core/components/ui/skeleton';
import { toast } from '@/core/utils/toast';
import { Image as ImageIcon } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { getAbsoluteMediaUrl, getPublicMediaUrl, toOptimizableSrc } from '@/core/utils/media';
import { cn } from '@/core/utils/cn';
import { stripHtml } from '@/core/utils/strip-html';
import { WishlistHeart } from '@/modules/shop/components/wishlist/WishlistHeart';
import { cardOpensProductPage, getProductPrimaryAction, priceOnRequestLabel, servicePricingType, usesConfigurator } from '@/core/utils/product-action';

export function ProductCardSkeleton() {
    return (
        <div className="flex flex-col border border-gray-100 rounded-xl bg-white overflow-hidden">
            <Skeleton className="aspect-square w-full bg-gray-100" />
            <div className="p-4 space-y-3">
                <Skeleton className="h-5 w-3/4 rounded bg-gray-100" />
                <Skeleton className="h-5 w-1/3 rounded bg-gray-100" />
                <Skeleton className="h-10 w-full rounded bg-gray-100 mt-2" />
            </div>
        </div>
    );
}

function StarRating({ value }: { value: number }) {
    const full = Math.floor(value);
    const half = value - full >= 0.5;
    return (
        <span className="flex items-center gap-0.5" aria-label={`Ocena ${value.toFixed(1)}`}>
            {Array.from({ length: 5 }).map((_, i) => (
                <svg key={i} className={`h-3.5 w-3.5 ${i < full ? 'text-amber-400' : i === full && half ? 'text-amber-300' : 'text-muted-foreground/30'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
            ))}
            <span className="text-[10px] ml-0.5 opacity-60">({value.toFixed(1)})</span>
        </span>
    );
}

export function ProductCard({ product, priority = false }: { product: any; priority?: boolean }) {
    const { t, currency } = useTranslation();
    const [isInCart, setIsInCart] = useState(false);

    useEffect(() => {
        if (product.product_type === 'variable' || !product.is_one_off) return;
        const checkCart = () => {
            const cart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
            setIsInCart(cart.some((item: any) => item.id === product.id && !item.variant_id));
        };
        checkCart();
        window.addEventListener('cart-updated', checkCart);
        return () => window.removeEventListener('cart-updated', checkCart);
    }, [product.id, product.product_type, product.is_one_off]);

    if (!product || !product.slug) return null;

    // A subscription plan or a service with "subscribe and save" tiers can't be quick-added from
    // a listing card — there's no room here for a tier/payment-mode picker. Checked BEFORE
    // preventDefault/stopPropagation below (unlike the variable/configurable early-return, which
    // intentionally leaves the click inert on this card): this one must fall through to the
    // card's own Link and actually open the product page.
    const needsSubscriptionPurchase = getProductPrimaryAction(product).type === 'view_product';
    // A numbered-seat ticket needs a seat picked on the product page first (same as ProductGrid):
    // quick-adding it here put a seatless ticket in the cart.
    const isSeatMapTicket = product.product_type === 'ticket' && !!product.has_seat_map;
    // Anything to pick on the product page first (variant, options, seat, weight, tier): the
    // button just opens it — it used to swallow the click for variants/options and do nothing.
    const opensProductPage = cardOpensProductPage(product);
    const pricingUnit = servicePricingType(product);
    const perKgSuffix = pricingUnit === 'weight' || pricingUnit === 'area'
        ? <span className="text-[0.6em] font-semibold opacity-60 ml-1">{pricingUnit === 'area' ? t('product.per_m2', '/ m²') : t('product.per_kg', '/ kg')}</span>
        : null;

    // Canonical product URL is /produkty/<slug> — no category path (see proxy.ts, which 301s
    // the old /produkty/<category>/<slug> form), so every link, the sitemap and the canonical
    // tag agree on one URL per product.
    const href = `/produkty/${product.slug}`;

    return (
        <IntentLink href={href} className="group block h-full">
            <div
                className={cn(
                    "flex flex-col h-full bg-white transition-all duration-300 relative overflow-hidden",
                    "hover:shadow-lg hover:-translate-y-1"
                )}
                style={{ borderRadius: '12px', border: '1px solid #f3f4f6' }}
            >
                {/* Image */}
                <div className="aspect-square bg-gray-50 flex items-center justify-center text-gray-300 relative overflow-hidden border-b border-gray-100">
                    {product.condition && (
                        <span className={`absolute top-2 left-2 z-10 px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest shadow-sm transition-all duration-300 product-badge-${product.condition}`}>
                            {t(`product.condition.${product.condition}`, product.condition)}
                        </span>
                    )}
                    <WishlistHeart productId={product.id} />
                    {(() => {
                        const images = product.images || [];
                        const mainImage = images.find((img: any) => img.is_main) || images[0];
                        const hoverExplicit = images.find((img: any) => img.is_hover_image);
                        const otherImages = images.filter((img: any) => img !== mainImage);
                        const secondaryImage = hoverExplicit && hoverExplicit !== mainImage ? hoverExplicit : (otherImages.length > 0 ? otherImages[0] : null);

                        if (mainImage) {
                            const rawPath = mainImage.path || mainImage.thumbnail_path;
                            const url = toOptimizableSrc(getAbsoluteMediaUrl(rawPath));
                            const secondaryUrl = secondaryImage ? toOptimizableSrc(getAbsoluteMediaUrl(secondaryImage.path || secondaryImage.thumbnail_path)) : null;

                            return (
                                <>
                                    <NextImage
                                        src={url}
                                        alt={mainImage.alt_text || product.name}
                                        fill
                                        unoptimized
                                        priority={priority}
                                        fetchPriority={priority ? "high" : "auto"}
                                        className={cn(
                                            "object-cover transition-all duration-500",
                                            secondaryUrl ? "group-hover:opacity-0" : "group-hover:scale-105"
                                        )}
                                    />
                                    {secondaryUrl && (
                                        <NextImage
                                            src={secondaryUrl}
                                            alt={secondaryImage.alt_text || `${product.name} - widok 2`}
                                            fill
                                            unoptimized
                                            className="object-cover transition-all duration-500 absolute inset-0 opacity-0 group-hover:opacity-100 group-hover:scale-105"
                                        />
                                    )}
                                </>
                            );
                        }
                        return <ImageIcon className="w-10 h-10 opacity-30 group-hover:scale-110 transition-transform duration-500" />;
                    })()}
                </div>

                {/* Content */}
                <div className="p-4 flex flex-col flex-1 gap-3">
                    <h3
                        className="font-bold text-[#111827] line-clamp-2 leading-tight"
                        style={{ fontSize: '16px' }}
                    >
                        {product.name}
                    </h3>

                    {product.rating != null && (
                        <div className="flex items-center gap-1">
                            <StarRating value={product.rating} />
                            {product.review_count > 0 && (
                                <span className="text-xs text-muted-foreground">({product.review_count})</span>
                            )}
                        </div>
                    )}

                    {(product.short_description || product.description) && (
                        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                            {stripHtml(product.short_description || product.description)}
                        </p>
                    )}

                    <div className="mt-auto pt-1 flex flex-col gap-3">
                        {product.brand?.logo_url && (
                            // Fixed-size box, not just a fixed height + w-auto/max-w cap: the
                            // width/height props above are a guessed aspect ratio (not the real
                            // logo's), so letting CSS width stay auto reserves the wrong box and
                            // reflows once the real (usually different) ratio decodes — see the
                            // identical bug fixed in Builder/ProductGrid.tsx's brand logo (a real
                            // brand logo there measured a 0.735 CLS contribution alone).
                            <div className="h-6 w-16 shrink-0 flex items-center">
                                <NextImage
                                    src={toOptimizableSrc(getAbsoluteMediaUrl(product.brand.logo_url))}
                                    alt={product.brand.name}
                                    width={64}
                                    height={24}
                                    unoptimized
                                    style={{ width: '100%', height: '100%' }}
                                    className="object-contain"
                                />
                            </div>
                        )}
                        {/* Price */}
                        <div className="flex flex-col text-[#111827]">
                            {product.price_on_request ? (
                                <span className="font-bold" style={{ fontSize: '16px' }}>{priceOnRequestLabel(product, t)}</span>
                            ) : product.product_type === 'bundle' ? (
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <span className="font-black" style={{ fontSize: '18px' }}>
                                            {(product.sales_price ?? product.price ?? 0).toFixed(2)} {currency}
                                        </span>
                                        {(product.items_regular_price_sum || product.price) > (product.sales_price ?? product.price ?? 0) && (
                                            <span className="text-[0.7em] opacity-50 line-through font-normal">
                                                {(product.items_regular_price_sum || product.price).toFixed(2)} {currency}
                                            </span>
                                        )}
                                    </div>
                                    {(product.savings_amount || ((product.items_regular_price_sum || 0) - (product.sales_price ?? product.price ?? 0))) > 0 && (
                                        <p className="text-[11px] font-black text-pink-600 bg-pink-50 px-2 py-0.5 rounded-md inline-block">
                                            {t('product.save_amount', 'Oszczędzasz')} {(product.savings_amount || ((product.items_regular_price_sum || 0) - (product.sales_price ?? product.price ?? 0))).toFixed(2)} {currency}
                                        </p>
                                    )}
                                </div>
                            ) : product.product_type === 'variable' || usesConfigurator(product) ? (
                                <span className="font-black" style={{ fontSize: '18px' }}>
                                    <span className="text-[0.65em] font-normal opacity-70 mr-1.5 uppercase tracking-wide">{t('product.from')}</span>
                                    {(product.sales_price ?? product.price).toFixed(2)} {currency}
                                    {perKgSuffix}
                                </span>
                            ) : product.sales_price !== null ? (
                                <div className="flex items-center gap-2">
                                    <span className="font-black bg-red-50 text-red-700 px-2 py-0.5 rounded-md" style={{ fontSize: '18px' }}>
                                        {product.sales_price.toFixed(2)} {currency}
                                    </span>
                                    <span className="text-[0.7em] opacity-50 line-through">{product.price.toFixed(2)} {currency}</span>
                                </div>
                            ) : (
                                <span className="font-black" style={{ fontSize: '18px' }}>
                                    {product.price.toFixed(2)} {currency}
                                    {perKgSuffix}
                                </span>
                            )}
                        </div>

                        <Button
                            className="w-full font-bold shadow-none hover:shadow-md transition-all text-[10px] xs:text-xs sm:text-sm px-1 sm:px-4 h-auto min-h-[32px] sm:min-h-[40px] whitespace-normal leading-tight"
                            disabled={!opensProductPage && product.is_one_off && isInCart}
                            onClick={(e) => {
                                if (opensProductPage) return;

                                e.preventDefault();
                                e.stopPropagation();

                                if (product.is_one_off && isInCart) {
                                    toast.error(t('product.one_off_limit', 'Ten produkt można dodać do koszyka tylko raz.'));
                                    return;
                                }

                                try {
                                    const cart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
                                    const cartItemId = String(product.id);
                                    const existingItemIndex = cart.findIndex((item: any) => (item.cartItemId === cartItemId) || (item.id === product.id && !item.variant_id));

                                    const mainImage = product.images?.find((img: any) => img.is_main) || product.images?.[0];
                                    const rawCartPath = mainImage ? (mainImage.thumbnail_path || mainImage.path) : null;
                                    const imageUrl = rawCartPath ? getPublicMediaUrl(rawCartPath) : null;

                                    if (existingItemIndex > -1) {
                                        if (product.is_one_off) {
                                            toast.error(t('product.one_off_limit', 'Ten produkt można dodać do koszyka tylko raz.'));
                                            return;
                                        }
                                        cart[existingItemIndex].quantity += 1;
                                        cart[existingItemIndex].cartItemId = cartItemId;
                                        cart[existingItemIndex].image = imageUrl;
                                    } else {
                                        const activePrice = product.sales_price !== null && product.sales_price !== undefined ? product.sales_price : product.price;
                                        cart.push({
                                            cartItemId,
                                            id: product.id,
                                            product_type: product.product_type,
                                            name: product.name,
                                            price: activePrice,
                                            image: imageUrl,
                                            slug: product.slug,
                                            quantity: 1,
                                            is_one_off: !!product.is_one_off
                                        });
                                    }

                                    localStorage.setItem('stag-cart', JSON.stringify(cart));
                                    window.dispatchEvent(new Event('cart-updated'));
                                    toast.success(t('product.add_to_cart_success'));
                                } catch (error) {
                                    console.error("Failed to add to cart", error);
                                    toast.error(t('product.add_to_cart_error'));
                                }
                            }}
                        >
                            {isSeatMapTicket
                                ? t('product.select_seat', 'Wybierz miejsce')
                                : product.product_type === 'ticket'
                                ? t('product.select_ticket', 'Wybierz bilet')
                                : product.product_type === 'configurable'
                                ? t('product.configure', 'Konfiguruj')
                                : usesConfigurator(product)
                                ? t('product.select_options', 'Wybierz opcje')
                                : servicePricingType(product) === 'weight' && !product.has_subscription_tiers
                                ? t('product.enter_weight', 'Podaj wagę')
                                : servicePricingType(product) === 'area' && !product.has_subscription_tiers
                                ? t('product.enter_dimensions', 'Podaj wymiary')
                                : needsSubscriptionPurchase
                                ? t('product.view_offer', 'Zobacz ofertę')
                                : product.product_type === 'variable'
                                    ? t('product.select_options')
                                    : product.product_type === 'bundle'
                                        ? t('product.add_bundle_to_cart', 'Dodaj zestaw do koszyka')
                                        : (product.is_one_off && isInCart
                                            ? t('product.already_in_cart', 'W koszyku')
                                            : t('product.add_to_cart'))}
                        </Button>

                    </div>
                </div>
            </div>
        </IntentLink>
    );
}
